import { type FC, type ReactNode, type Ref, useMemo } from 'react'
import RjsfForm from '@rjsf/core'
import { type ErrorSchema, type Experimental_DefaultFormStateBehavior, type RJSFSchema } from '@rjsf/utils'
import { type JsonObject } from './mergePatch'
import { buildUiSchema } from './uiSchema'
import { type ParametersFormContext } from './formContext'
import { parameterTemplates } from './rjsf/parameterTemplates'
import { parameterWidgets } from './rjsf/parameterWidgets'
import { PARAMETERS_DEFAULT_FORM_STATE, parametersValidator } from './rjsf/validator'
import styles from './ParametersForm.module.scss'

const noopSetFieldUploading = () => {}

export type ParametersFormInstance = RjsfForm<JsonObject, RJSFSchema, ParametersFormContext>

interface ParametersRjsfFormProps {
  idPrefix: string
  formRef?: Ref<ParametersFormInstance>
  schema: RJSFSchema
  formData: JsonObject
  layer: 'account' | 'user'
  setFieldUploading?: (fieldId: string, uploading: boolean) => void
  generation?: number
  defaultFormStateBehavior?: Experimental_DefaultFormStateBehavior
  extraErrors?: ErrorSchema
  disabled?: boolean
  readonly?: boolean
  onChange?: (formData: JsonObject) => void
  onSubmit?: () => void
  onError?: () => void
  children?: ReactNode
}

export const ParametersRjsfForm: FC<ParametersRjsfFormProps> = ({
  idPrefix,
  formRef,
  schema,
  formData,
  layer,
  setFieldUploading = noopSetFieldUploading,
  generation = 0,
  defaultFormStateBehavior,
  extraErrors,
  disabled,
  readonly,
  onChange,
  onSubmit,
  onError,
  children,
}) => {
  const uiSchema = useMemo(() => buildUiSchema(schema), [schema])
  const formContext: ParametersFormContext = { rootId: idPrefix, layer, generation, setFieldUploading }

  return (
    <div className={styles.form}>
      <RjsfForm<JsonObject, RJSFSchema, ParametersFormContext>
        ref={formRef}
        idPrefix={idPrefix}
        formContext={formContext}
        schema={schema}
        uiSchema={uiSchema}
        validator={parametersValidator}
        widgets={parameterWidgets}
        templates={parameterTemplates}
        formData={formData}
        experimental_defaultFormStateBehavior={{ ...PARAMETERS_DEFAULT_FORM_STATE, ...defaultFormStateBehavior }}
        extraErrors={extraErrors}
        onChange={(event) => onChange?.(event.formData ?? {})}
        onSubmit={() => onSubmit?.()}
        onError={() => onError?.()}
        liveValidate={false}
        showErrorList={false}
        noHtml5Validate
        disabled={disabled}
        readonly={readonly}
      >
        {children ?? <></>}
      </RjsfForm>
    </div>
  )
}
